import { useDebounceFunc } from "client/hooks/utils/useDebounceFunc";
import { useToggle } from "client/hooks/utils/useToggle";
import { getSocket } from "client/utils/getSocket";
import { downloadFromLink } from "client/utils/downloadFromLink";
import { DEFAULT_TOAST_SHOW_TIME } from "components/common/Toast/ToastRenderer";
import { useToast } from "components/common/Toast/useToast";
import { useEffect } from "react";
import { deleteImage, setQuality, setSelectedImage, setTooltipBounceImageId } from "store/features/compress/actions";
import { compressSelector } from "store/features/compress/selectors/compressSelector";
import { CompressImage, CompressState } from "store/features/compress/types";
import { store, useAppDispatch, useAppSelector } from "store/store";
import { useCompress } from "../../useCompress";
import { getFCUrl } from "client/utils/getFCUrl";
import { useMountEffect } from "client/hooks/utils/useMountEffect";
import { getImageExtension } from "client/utils/getImageExtension";
import { getCompressionSettings } from "store/features/compress/utils/getCompressionSettings";

let createdInitialICompressTasks = false;
let settingsClickTimeout: NodeJS.Timeout | null = null;

export const usePreviewList = () => {
    const { images, selectedImageId } = useAppSelector(compressSelector, equalityFn);
    const selectedImage = images.find((_image) => _image.id === selectedImageId) ?? null;

    const dispatch = useAppDispatch();
    const { toast } = useToast();

    const { isOpen, toggle, onClose: close, open } = useToggle();

    const { compressImage } = useCompress();

    const handleDelete = (id: string) => {
        if (store.getState().compress.downloadButtonState.state !== "active") return;
        dispatch(deleteImage(id));
        toast({
            closeable: true,
            position: "top-right",
            status: "error",
            time: DEFAULT_TOAST_SHOW_TIME,
            text: "Image Deleted",
        });
    };

    const handleSelect = (id: string) => {
        const image = images.find((_image) => _image.id === id);
        if (!image || image.state !== "compressed") return;

        dispatch(setSelectedImage(id));
        // open modal here
        open();
    };

    const handleApply = () => {
        if (!selectedImage) return;

        dispatch(setQuality(selectedImage.sliderValue));
    };

    const handleDownload = (image: CompressImage) => {
        if (image.state !== "compressed" || !image.compressed) return;
        downloadFromLink(getFCUrl(image.compressed.src), image.title, true);
    };

    const handleSettingsClick = (image: CompressImage) => {
        dispatch(setTooltipBounceImageId({ imageId: image.id }));

        settingsClickTimeout && clearTimeout(settingsClickTimeout);
        settingsClickTimeout = setTimeout(() => {
            dispatch(setTooltipBounceImageId({ imageId: null }));
        }, 3000);
    };

    const debounce = useDebounceFunc();

    useMountEffect(() => {
        if (createdInitialICompressTasks) return;
        createdInitialICompressTasks = true;

        debounce(async () => {
            await getSocket();
            images.forEach((i) => {
                i.importTask &&
                    compressImage(
                        i,
                        i.importTask,
                        getCompressionSettings(
                            getImageExtension(i.file.file),
                            store.getState().compress.compressionSettings,
                        ),
                        i.file.file.name,
                    );
            });
        });
    }, usePreviewList.name);

    useEffect(() => {
        createdInitialICompressTasks = false;
    }, []);

    return {
        images,
        handleSelect,
        handleDelete,
        selectedImage,
        isOpen,
        toggle,
        close,
        handleApply,
        handleDownload,
        handleSettingsClick,
    };
};

const equalityFn = (left: CompressState, right: CompressState) => {
    if (JSON.stringify(left.images) !== JSON.stringify(right.images) || left.selectedImageId !== right.selectedImageId)
        return false;

    return true;
};
